import {
  resolveReservationPolicy,
  type ReservationPolicySettingsSource,
} from './organization-settings/resolve-reservation-policy';
import { convertUtcToOrgTimezone } from './timezoneUtils';

/**
 * Days Between Reservations: range-relative availability model.
 *
 * Each booking occupies its calendar days [depKey .. retKey] plus a
 * turnaround buffer of `bufferDays` whole days after its scheduled return
 * (governed by the booking's own Reservation Policy profile). The single
 * rule, applied between any two consecutive reservations on a unit, is:
 *
 *   next departure date >= scheduled return date + bufferDays
 *
 * At bufferDays = 0 ("same-day departure allowed") a multi-day booking's
 * endpoints stay shareable as "flips": a new reservation may RETURN on the
 * booking's departure day, or DEPART on the booking's return day. A
 * single-day booking (dep === ret) occupies both halves of its day and has
 * no shareable half. At bufferDays >= 1 there are no flips: the blocked span
 * is [dep .. ret + bufferDays - 1] and the earliest next departure is
 * ret + bufferDays.
 *
 * 2026-07-28 (supersedes QA-1261): a manual unavailability block (a
 * reservations row with status 'Unavailable') follows the SAME rules as a
 * booking: a multi-day block shares its endpoint days as flips; a
 * single-day block still owns its whole day via the single-day rule.
 *
 * All day keys are YYYY-MM-DD strings in the organization timezone, so they
 * compare correctly with plain string comparison. Pure module, so it is safe for the
 * SDK bundle and the client pickers.
 *
 * MATCHED PAIR: the AUTHORITATIVE model lives in SQL:
 * public.reservation_spans_conflict_buffered and
 * public.resolve_buffer_days_from_profiles in
 * src/lib/reservations/check_overlap.sql, enforced by the
 * check_reservation_overlap RPC, the prevent_overlap.sql trigger, and
 * get_available_rvs_for_date_range. This module mirrors those rules for
 * client-side date pickers; keep the two in sync.
 */

export interface TurnaroundBooking {
  id?: string;
  /** Departure (check-out) day key, org timezone. */
  depKey: string;
  /** Scheduled return (check-in) day key, org timezone. */
  retKey: string;
  /** Buffer governing THIS booking's turnaround after its return (0-14). */
  bufferDays: number;
}

/** Booking span as returned by check_reservation_overlap / SDK payloads. */
export interface RawBookingSpan {
  id?: string | null;
  check_out_date: string; // UTC ISO departure
  check_in_date: string; // UTC ISO scheduled return
  status?: string | null;
}

const MAX_TURNAROUND_BUFFER_DAYS = 14;

function normalizeBufferDays(value: unknown): number {
  const num = typeof value === 'number' ? Math.trunc(value) : NaN;
  if (!Number.isFinite(num)) return 0;
  return Math.min(Math.max(num, 0), MAX_TURNAROUND_BUFFER_DAYS);
}

/** Add N calendar days to a YYYY-MM-DD key. UTC math, so no DST edge cases. */
export function addDaysToKey(dayKey: string, days: number): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days, 12));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

/** Booking input with a pre-resolved buffer (UTC ISO timestamps). */
export interface TurnaroundBookingInput {
  id?: string;
  /** UTC ISO departure (check-out) of the existing reservation. */
  checkOut: string;
  /** UTC ISO scheduled return (check-in) of the existing reservation. */
  checkIn: string;
  /** Buffer days governing this booking (0 = same-day allowed). */
  bufferDays: number;
}

/**
 * Convert pre-resolved booking inputs (UTC ISO timestamps) to day-keyed
 * bookings in the organization timezone.
 */
export function toTurnaroundBookings(
  inputs: TurnaroundBookingInput[] | null | undefined,
  timezone: string,
): TurnaroundBooking[] {
  if (!Array.isArray(inputs) || inputs.length === 0) return [];
  const bookings: TurnaroundBooking[] = [];
  for (const input of inputs) {
    if (!input?.checkOut || !input?.checkIn) continue;
    try {
      const depKey = convertUtcToOrgTimezone(input.checkOut, timezone).dateOnly;
      const retKey = convertUtcToOrgTimezone(input.checkIn, timezone).dateOnly;
      if (!depKey || !retKey) continue;
      bookings.push({
        id: input.id,
        depKey,
        // Guard malformed data; a return before departure collapses to a
        // single-day booking rather than producing an inverted span.
        retKey: retKey >= depKey ? retKey : depKey,
        bufferDays: normalizeBufferDays(input.bufferDays),
      });
    } catch {
      // Unparseable span, so skip rather than poison the whole calendar.
    }
  }
  return bookings;
}

/**
 * Resolve each raw booking span's buffer from its own governing profile
 * (matched by rvId + the booking's departure date), keeping the UTC instants.
 * This is the shape DateRangePicker's `turnaroundBookings` prop takes.
 */
export function resolveBookingSpanBuffers(
  spans: RawBookingSpan[] | null | undefined,
  settings: ReservationPolicySettingsSource | null | undefined,
  rvId: string | null | undefined,
  timezone: string,
): TurnaroundBookingInput[] {
  if (!Array.isArray(spans)) return [];
  return spans
    .filter((span) => span?.check_out_date && span?.check_in_date)
    .map((span) => ({
      id: span.id ?? undefined,
      checkOut: span.check_out_date,
      checkIn: span.check_in_date,
      bufferDays: resolveReservationPolicy(settings, {
        rvId: rvId ?? null,
        date: span.check_out_date,
        timezone,
      }).daysBetweenReservations,
    }));
}

/**
 * Convert raw booking spans (UTC ISO timestamps) into day-keyed bookings,
 * resolving the buffer for each booking from its own governing profile.
 */
export function buildTurnaroundBookings(
  spans: RawBookingSpan[] | null | undefined,
  settings: ReservationPolicySettingsSource | null | undefined,
  rvId: string | null | undefined,
  timezone: string,
): TurnaroundBooking[] {
  return toTurnaroundBookings(
    resolveBookingSpanBuffers(spans, settings, rvId, timezone),
    timezone,
  );
}

/**
 * Buffer governing the reservation currently being formed, resolved from its
 * departure date (same context convention as resolveReservationPolicy).
 * Constrains how close the new reservation's return may sit before a later
 * booking's departure.
 */
export function resolveNewReservationBufferDays(
  settings: ReservationPolicySettingsSource | null | undefined,
  rvId: string | null | undefined,
  departureUtcIso: string | null | undefined,
  timezone: string,
): number {
  const policy = resolveReservationPolicy(settings, {
    rvId: rvId ?? null,
    date: departureUtcIso ?? null,
    timezone,
  });
  return normalizeBufferDays(policy.daysBetweenReservations);
}

/** Last day of a booking's blocked span (return day + buffer tail). */
function lastBlockedKey(b: TurnaroundBooking): string {
  return b.bufferDays > 0 ? addDaysToKey(b.retKey, b.bufferDays - 1) : b.retKey;
}

function isSingleDay(b: TurnaroundBooking): boolean {
  return b.depKey === b.retKey;
}

/**
 * Is the day blocked as a standalone candidate (first click)? Blocked when it
 * falls in any booking's unavailable span: the strict interior at buffer 0
 * (endpoints stay selectable as flips; a single-day booking blocks its whole
 * day), or [dep .. ret + buffer - 1] at buffer >= 1.
 */
export function isInteriorBlocked(
  dayKey: string,
  bookings: TurnaroundBooking[],
): boolean {
  for (const b of bookings) {
    if (b.bufferDays === 0) {
      if (isSingleDay(b)) {
        if (dayKey >= b.depKey && dayKey <= b.retKey) return true;
      } else if (dayKey > b.depKey && dayKey < b.retKey) {
        return true;
      }
    } else if (dayKey >= b.depKey && dayKey <= lastBlockedKey(b)) {
      return true;
    }
  }
  return false;
}

/**
 * Which flip affordances does this day carry?
 * - asStart: some buffer-0 multi-day booking RETURNS here, so a new reservation
 *   may depart this day (unit back in the morning, free in the afternoon).
 * - asEnd: some multi-day booking DEPARTS here and the new reservation's own
 *   buffer is 0, so the new reservation may return this day (free in the
 *   morning, occupied in the afternoon).
 */
export function flipRole(
  dayKey: string,
  bookings: TurnaroundBooking[],
  newReservationBufferDays = 0,
): { asStart: boolean; asEnd: boolean } {
  let asStart = false;
  let asEnd = false;
  for (const b of bookings) {
    if (isSingleDay(b)) continue;
    if (b.bufferDays === 0 && dayKey === b.retKey) asStart = true;
    if (
      normalizeBufferDays(newReservationBufferDays) === 0 &&
      dayKey === b.depKey
    ) {
      asEnd = true;
    }
  }
  return { asStart, asEnd };
}

/** First-click legality: any day not inside a blocked span. */
export function isLegalAnchor(
  dayKey: string,
  bookings: TurnaroundBooking[],
): boolean {
  return !isInteriorBlocked(dayKey, bookings);
}

/**
 * Given a chosen departure (anchor), may the range end on `endKey`? The
 * formed range, extended by the new reservation's own buffer tail, must
 * not overlap any booking's blocked span, except the two permitted flip
 * touches at buffer 0.
 */
export function isLegalEndGivenAnchor(
  anchorKey: string,
  endKey: string,
  bookings: TurnaroundBooking[],
  newReservationBufferDays = 0,
): boolean {
  if (endKey < anchorKey) return false;
  const newBuffer = normalizeBufferDays(newReservationBufferDays);
  const effectiveEnd =
    newBuffer > 0 ? addDaysToKey(endKey, newBuffer - 1) : endKey;
  for (const b of bookings) {
    const overlap = !(effectiveEnd < b.depKey || anchorKey > lastBlockedKey(b));
    if (!overlap) continue;
    // A single-day booking owns both halves of its day, so no flip touches.
    if (isSingleDay(b)) return false;
    // Flip: new reservation RETURNS on the booking's departure day. Needs the
    // new reservation's own buffer to be 0 (its return must allow a same-day
    // next departure, the booking's).
    if (newBuffer === 0 && endKey === b.depKey && anchorKey < b.depKey) {
      continue;
    }
    // Flip: new reservation DEPARTS on the booking's return day. Needs the
    // booking's buffer to be 0.
    if (b.bufferDays === 0 && anchorKey === b.retKey && endKey > b.retKey) {
      continue;
    }
    return false;
  }
  return true;
}

/**
 * Full-range legality. Client-side mirror of the SQL write-time checks
 * (server enforcement happens in check_reservation_overlap / the
 * prevent_overlap.sql trigger).
 */
export function isLegalRange(
  depKey: string,
  retKey: string,
  bookings: TurnaroundBooking[],
  newReservationBufferDays = 0,
): boolean {
  return (
    retKey >= depKey &&
    isLegalAnchor(depKey, bookings) &&
    isLegalEndGivenAnchor(depKey, retKey, bookings, newReservationBufferDays)
  );
}

/**
 * The same bookings with every turnaround buffer stripped to 0, i.e. the
 * strict whole-day occupancy floor (physical double-booking, flips allowed).
 * This is the part of availability that is HARD in EVERY interface, the CI and
 * the UI alike; only the buffer TAIL on top of it is soft for operators. Used
 * to separate a hard overlap from an overridable Days Between Reservations
 * conflict.
 */
export function toStrictBookings(
  bookings: TurnaroundBooking[],
): TurnaroundBooking[] {
  return bookings.map((b) => (b.bufferDays === 0 ? b : { ...b, bufferDays: 0 }));
}

export type RangeClassification = 'clear' | 'buffer' | 'overlap';

/**
 * Classify a candidate range against existing bookings, separating the
 * never-overridable floor from the operator-overridable buffer:
 *   - 'overlap': strict whole-day conflict (physical double-booking or an
 *     illegal flip). HARD everywhere: the CI and the UI both block it.
 *   - 'buffer':  clears the strict floor but violates a Days Between
 *     Reservations buffer tail (this booking's, the candidate's, or both).
 *     HARD in the CI (the server gate rejects it for public context), SOFT in
 *     the UI, where the operator sees a non-blocking warning and may proceed.
 *   - 'clear':   legal even with every buffer applied.
 *
 * The candidate's own buffer participates only in the buffered test, never the
 * strict floor. Mirrors the server's buffered-minus-strict split in
 * check_reservation_overlap (has_critical_overlap vs has_buffer_conflict).
 */
export function classifyCandidateRange(
  depKey: string,
  retKey: string,
  bookings: TurnaroundBooking[],
  newReservationBufferDays = 0,
): RangeClassification {
  if (retKey < depKey) return 'overlap';
  if (!isLegalRange(depKey, retKey, toStrictBookings(bookings), 0)) {
    return 'overlap';
  }
  if (!isLegalRange(depKey, retKey, bookings, newReservationBufferDays)) {
    return 'buffer';
  }
  return 'clear';
}

/**
 * End-to-end classification of a candidate reservation against raw blocking
 * spans (the shape check_reservation_overlap returns), resolving every buffer
 * from its governing profile. One call answers "is this range clear, a soft
 * buffer conflict, or a hard overlap?" for any operator surface (the date
 * field, the submit gate, the calendar dialog). Returns 'clear' when the dates
 * are incomplete so callers don't false-warn on partial input.
 */
export function classifyReservationAgainstSpans({
  spans,
  settings,
  rvId,
  checkOutUtc,
  checkInUtc,
  timezone,
}: {
  spans: RawBookingSpan[] | null | undefined;
  settings: ReservationPolicySettingsSource | null | undefined;
  rvId: string | null | undefined;
  checkOutUtc: string | null | undefined;
  checkInUtc: string | null | undefined;
  timezone: string;
}): RangeClassification {
  if (!checkOutUtc || !checkInUtc) return 'clear';
  let depKey: string | undefined;
  let retKey: string | undefined;
  try {
    depKey = convertUtcToOrgTimezone(checkOutUtc, timezone).dateOnly;
    retKey = convertUtcToOrgTimezone(checkInUtc, timezone).dateOnly;
  } catch {
    return 'clear';
  }
  if (!depKey || !retKey) return 'clear';
  return classifyCandidateRange(
    depKey,
    retKey,
    buildTurnaroundBookings(spans, settings, rvId, timezone),
    resolveNewReservationBufferDays(settings, rvId, checkOutUtc, timezone),
  );
}

/**
 * The first booking responsible for a buffer-class conflict on a candidate
 * range: the neighbour whose turnaround buffer the range falls inside. Used to
 * name the buffer length and the neighbouring return date in operator warnings.
 * Returns null when the range is clear or a hard overlap (no single soft
 * culprit).
 */
export function findBufferConflict(
  depKey: string,
  retKey: string,
  bookings: TurnaroundBooking[],
  newReservationBufferDays = 0,
): TurnaroundBooking | null {
  for (const bk of bookings) {
    if (
      classifyCandidateRange(depKey, retKey, [bk], newReservationBufferDays) ===
      'buffer'
    ) {
      return bk;
    }
  }
  return null;
}

/**
 * Per-day soft-buffer split for the UI picker, evaluated for whichever endpoint
 * is currently being chosen (anchor phase vs. return phase). A day is:
 *   - 'blocked'    if it conflicts under the strict floor (hard, so disable it);
 *   - 'buffer'     if it clears the strict floor but conflicts under the
 *     buffered model (soft: selectable, but mark it and warn on use);
 *   - null         if it is freely selectable.
 * `anchorKey` is the chosen start when selecting the return, else null.
 */
export function dayBufferState(
  dayKey: string,
  bookings: TurnaroundBooking[],
  newReservationBufferDays = 0,
  anchorKey: string | null = null,
): 'blocked' | 'buffer' | null {
  const strict = toStrictBookings(bookings);

  // Return phase applies only when the chosen anchor is itself a clear, legal
  // departure. If the operator soft-anchored on a buffer/blocked day, the range
  // [anchor, day] inherits the anchor's own constant conflict for every forward
  // day, hatching all of them. Fall back to per-day anchor classification so
  // each forward day shows its own buffer status instead of the anchor's. With
  // a clear anchor no behind-booking can taint the range, so only genuine
  // forward conflicts (the candidate's own buffer, an upcoming booking) mark.
  const useAnchor =
    !!anchorKey && dayKey >= anchorKey && isLegalAnchor(anchorKey, bookings);

  // Anchor phase classifies the MINIMAL single-day candidate [day, day]. Using
  // the full range legality (not just isLegalAnchor) makes the candidate's own
  // buffer tail visible BEFORE a later booking's departure (the pre-departure
  // hatched band), so the days that trigger the soft-buffer warning are marked,
  // matching the bilateral band the Calendar page paints (QA-1246). isLegalAnchor
  // alone only covers a booking's own blocked span [dep .. ret + buffer - 1]
  // (the after-return tail), never the days ahead of a departure.
  const strictBlocked = useAnchor
    ? !isLegalEndGivenAnchor(anchorKey as string, dayKey, strict, 0)
    : !isLegalRange(dayKey, dayKey, strict, 0);
  if (strictBlocked) return 'blocked';

  const bufferedBlocked = useAnchor
    ? !isLegalEndGivenAnchor(
        anchorKey as string,
        dayKey,
        bookings,
        newReservationBufferDays,
      )
    : !isLegalRange(dayKey, dayKey, bookings, newReservationBufferDays);
  return bufferedBlocked ? 'buffer' : null;
}

/**
 * Static visual membership of a day in any booking's turnaround buffer band,
 * mirroring the Calendar page's drawGridBufferBand exactly: a booking with
 * buffer N >= 1 paints a hatched band on BOTH ends:
 *   - after its return:    [retKey .. retKey + N - 1]
 *   - before its departure: [depKey - N + 1 .. depKey]
 * Both ranges are INCLUSIVE of the booking's own return/departure endpoint
 * cells, so the departure and return days carry the band just as the grid bar
 * leaves a hatched half there. Buffer 0 has no band. Pure presentation: the
 * width keys off each booking's OWN buffer (the same value the grid uses),
 * independent of any candidate being formed. Used to hatch the New Reservation
 * picker identically to the calendar grid (QA-1246).
 */
export function inTurnaroundBufferBand(
  dayKey: string,
  bookings: TurnaroundBooking[],
): boolean {
  for (const b of bookings) {
    if (b.bufferDays < 1) continue;
    const postEnd = addDaysToKey(b.retKey, b.bufferDays - 1);
    if (dayKey >= b.retKey && dayKey <= postEnd) return true;
    const preStart = addDaysToKey(b.depKey, -(b.bufferDays - 1));
    if (dayKey >= preStart && dayKey <= b.depKey) return true;
  }
  return false;
}
